import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { defer, Observable } from "rxjs";
import { finalize, shareReplay } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { PayElectivePayGroupBeneficiaryPagination } from "src/app/shared/models/employee.interface";
import {
	Employee,
	EmployeeCreateDTO,
	EmployeePagination,
	EmployeeReportPagination
} from "../../../shared/models/employee.interface";
import { DeactivateEmployeesBulkResponse } from "../types/accounts";

@Injectable({
	providedIn: "root"
})
export class PayElectiveApiService {
	private sortField = "data.lastName";
	private sortDir = "ASC";
	private pendingEmployeeRequests = new Map<string, Observable<EmployeePagination>>();
	private employeeListCache = new Map<string, Observable<EmployeePagination>>();

	constructor(private http: HttpClient) {}

	getEmployeesByPaygroupId(
		id: string,
		pageSize: number,
		pageNumber: number,
		provider = "",
		status = "",
		queryString = "",
		hasBank: boolean | null = null,
		hasCard: boolean | null = null
	): Observable<EmployeePagination> {
		const normalizedProvider = provider === "all" ? "" : provider;
		const requestKey = JSON.stringify([
			id,
			pageSize,
			pageNumber,
			status,
			normalizedProvider,
			queryString,
			hasBank,
			hasCard
		]);
		if (pageSize === -1) {
			const cachedEmployeeList = this.employeeListCache.get(requestKey);
			if (cachedEmployeeList) return cachedEmployeeList;
		}

		const pendingRequest = this.pendingEmployeeRequests.get(requestKey);
		if (pendingRequest) return pendingRequest;

		const request$ = defer(() =>
			this.http.get<EmployeePagination>(
				`${environment.apiUri}/v1/employees?payGroupIds=${id}&pageSize=${pageSize}&pageNumber=${pageNumber}&statuses=${status}&providerName=${normalizedProvider}&queryString=${queryString}&havingBank=${hasBank}&havingCard=${hasCard}&sortField=${this.sortField}&sortDir=${this.sortDir}`
			)
		).pipe(
			finalize(() => this.pendingEmployeeRequests.delete(requestKey)),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		this.pendingEmployeeRequests.set(requestKey, request$);
		if (pageSize === -1) this.employeeListCache.set(requestKey, request$);
		return request$;
	}

	getEmployeesPayGroupData(
		customerId: string,
		legalEntityId: string[],
		paygroups: string[]
	): Observable<PayElectivePayGroupBeneficiaryPagination> {
		return this.http.get<PayElectivePayGroupBeneficiaryPagination>(
			`${environment.apiUri}/v3/employees/reports/beneficiaries?customerIds=${customerId}&legalEntityIds=${legalEntityId}&payGroupIds=${paygroups}`
		);
	}

	getEmployeeReport(customerId: string, payGroupId: string): Observable<EmployeeReportPagination> {
		return this.http.get<EmployeeReportPagination>(
			`${environment.apiUri}/v3/employees/reports/beneficiaries?payGroupIds=${payGroupId}&customerIds=${customerId}`
		);
	}

	createNewEmployee(employee: EmployeeCreateDTO): Observable<Employee> {
		return this.http
			.post<Employee>(`${environment.apiUri}/v1/employees`, employee)
			.pipe(finalize(() => this.employeeListCache.clear()));
	}

	updateEmployee(employee: EmployeeCreateDTO): Observable<Employee> {
		return this.http
			.put<Employee>(`${environment.apiUri}/v1/employees`, employee)
			.pipe(finalize(() => this.employeeListCache.clear()));
	}

	deactivateEmployeesBulk(
		employeeExternalIds: string[],
		providerName: string,
		legalEntityExternalId: string,
		payGroupExternalIds: string[]
	): Observable<DeactivateEmployeesBulkResponse> {
		return this.http
			.delete<DeactivateEmployeesBulkResponse>(`${environment.apiUri}/v1/employees/pay-methods/deactivate`, {
				body: { employeeExternalIds, providerName, legalEntityExternalId, payGroupExternalIds }
			})
			.pipe(finalize(() => this.employeeListCache.clear()));
	}
}
